import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const FinalPayCalculator = ({ departure, onClose }) => {
  const [calculations, setCalculations] = useState({
    basicSalary: 12500,
    annualLeaveBalance: 15,
    sickLeaveBalance: 5,
    longServiceLeave: 0,
    proRatedBonus: 2500,
    noticePayDays: 30,
    severancePay: 0
  });

  const [finalPay, setFinalPay] = useState(null);

  const calculateFinalPay = () => {
    const dailyRate = calculations?.basicSalary / 22; // 22 working days per month

    const annualLeavePayment = calculations?.annualLeaveBalance * dailyRate;
    const sickLeavePayment = calculations?.sickLeaveBalance * dailyRate;
    const longServiceLeavePayment = calculations?.longServiceLeave * dailyRate;
    const noticePayment = calculations?.noticePayDays * dailyRate;

    const grossFinalPay =
      calculations?.basicSalary +
      annualLeavePayment +
      sickLeavePayment +
      longServiceLeavePayment +
      calculations?.proRatedBonus +
      noticePayment +
      calculations?.severancePay;

    // PNG tax calculation (simplified)
    const taxableIncome = grossFinalPay;
    let tax = 0;
    if (taxableIncome > 250000) {
      tax = (taxableIncome - 250000) * 0.42 + 70000;
    } else if (taxableIncome > 70000) {
      tax = (taxableIncome - 70000) * 0.30 + 12500;
    } else if (taxableIncome > 12500) {
      tax = (taxableIncome - 12500) * 0.25;
    }

    const netFinalPay = grossFinalPay - tax;

    setFinalPay({
      basicSalary: calculations?.basicSalary,
      annualLeavePayment,
      sickLeavePayment,
      longServiceLeavePayment,
      proRatedBonus: calculations?.proRatedBonus,
      noticePayment,
      severancePay: calculations?.severancePay,
      grossFinalPay,
      tax,
      netFinalPay
    });
  };

  const handleInputChange = (field, value) => {
    setCalculations(prev => ({ ...prev, [field]: parseFloat(value) || 0 }));
  };

  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="bg-amber-100 p-2 rounded-lg">
            <Icon name="DollarSign" size={24} className="text-amber-600" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">Final Pay Calculator</h2>
            <p className="text-sm text-gray-600">{departure?.employeeName}</p>
          </div>
        </div>
        <Button onClick={onClose} variant="outline" size="sm">
          <Icon name="X" size={16} className="mr-1" />
          Close
        </Button>
      </div>

      {/* Input Form */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Basic Salary (PGK)
          </label>
          <Input
            type="number"
            value={calculations?.basicSalary}
            onChange={(e) => handleInputChange('basicSalary', e?.target?.value)}
            placeholder="Enter basic salary"
          />
        </div>
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Annual Leave Balance (Days)
          </label>
          <Input
            type="number"
            value={calculations?.annualLeaveBalance}
            onChange={(e) => handleInputChange('annualLeaveBalance', e?.target?.value)}
            placeholder="Enter leave days"
          />
        </div>
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Sick Leave Balance (Days)
          </label>
          <Input
            type="number"
            value={calculations?.sickLeaveBalance}
            onChange={(e) => handleInputChange('sickLeaveBalance', e?.target?.value)}
            placeholder="Enter sick leave days"
          />
        </div>
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Long Service Leave (Days)
          </label>
          <Input
            type="number"
            value={calculations?.longServiceLeave}
            onChange={(e) => handleInputChange('longServiceLeave', e?.target?.value)}
            placeholder="Enter long service leave"
          />
        </div>
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Pro-Rated Bonus (PGK)
          </label>
          <Input
            type="number"
            value={calculations?.proRatedBonus}
            onChange={(e) => handleInputChange('proRatedBonus', e?.target?.value)}
            placeholder="Enter bonus amount"
          />
        </div>
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Notice Pay (Days)
          </label>
          <Input
            type="number"
            value={calculations?.noticePayDays}
            onChange={(e) => handleInputChange('noticePayDays', e?.target?.value)}
            placeholder="Enter notice days"
          />
        </div>
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Severance Pay (PGK)
          </label>
          <Input
            type="number"
            value={calculations?.severancePay}
            onChange={(e) => handleInputChange('severancePay', e?.target?.value)}
            placeholder="Enter severance pay"
          />
        </div>
      </div>

      {/* Calculate Button */}
      <div className="mb-6">
        <Button
          onClick={calculateFinalPay}
          className="w-full bg-gradient-to-r from-amber-600 to-amber-700 text-white"
        >
          <Icon name="Calculator" size={20} className="mr-2" />
          Calculate Final Pay
        </Button>
      </div>

      {/* Results */}
      {finalPay && (
        <div className="border-t border-gray-200 pt-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4">Final Pay Breakdown</h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">Basic Salary</span>
              <span className="font-semibold text-gray-900">
                PGK {finalPay?.basicSalary?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">Annual Leave Payment</span>
              <span className="font-semibold text-gray-900">
                PGK {finalPay?.annualLeavePayment?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">Sick Leave Payment</span>
              <span className="font-semibold text-gray-900">
                PGK {finalPay?.sickLeavePayment?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            </div>
            {finalPay?.longServiceLeavePayment > 0 && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Long Service Leave</span>
                <span className="font-semibold text-gray-900">
                  PGK {finalPay?.longServiceLeavePayment?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
            )}
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">Pro-Rated Bonus</span>
              <span className="font-semibold text-gray-900">
                PGK {finalPay?.proRatedBonus?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">Notice Payment</span>
              <span className="font-semibold text-gray-900">
                PGK {finalPay?.noticePayment?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            </div>
            {finalPay?.severancePay > 0 && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Severance Pay</span>
                <span className="font-semibold text-gray-900">
                  PGK {finalPay?.severancePay?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
            )}
            <div className="border-t border-gray-200 pt-3 flex items-center justify-between">
              <span className="font-semibold text-gray-700">Gross Final Pay</span>
              <span className="font-bold text-gray-900 text-lg">
                PGK {finalPay?.grossFinalPay?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-red-600">PNG Income Tax</span>
              <span className="font-semibold text-red-600">
                - PGK {finalPay?.tax?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            </div>
            <div className="bg-gradient-to-r from-amber-50 to-amber-100 rounded-lg p-4 border border-amber-200">
              <div className="flex items-center justify-between">
                <span className="font-bold text-amber-900 text-lg">Net Final Pay</span>
                <span className="font-bold text-amber-900 text-2xl">
                  PGK {finalPay?.netFinalPay?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
            </div>
          </div>

          {/* Compliance Note */}
          <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Icon name="Info" size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-blue-900 mb-1">
                  PNG Employment Act Compliance
                </p>
                <p className="text-xs text-blue-700">
                  This calculation includes all statutory entitlements under PNG Employment Act.
                  Final pay must be processed within 7 days of termination date.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FinalPayCalculator;